import { CdkTrapFocus } from '@angular/cdk/a11y';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { IconComponent } from '../icon/icon.component';
import { IconName } from '../icon/icons';
import { uniqueId } from '../form/ids';

const POSITIONS: ConnectedPosition[] = [
  { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
];

/**
 * Pannello a comparsa legato a un pulsante (CDK Overlay): titolo, contenuto proiettato, fuoco
 * intrappolato finché è aperto, chiusura con Esc o fuori dal pannello e ritorno del fuoco al
 * pulsante. L'aspetto `search` mostra il pulsante come il campo di ricerca della testata.
 */
@Component({
  selector: 'bs-popover',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CdkConnectedOverlay, CdkOverlayOrigin, CdkTrapFocus, IconComponent],
  host: { class: 'inline-flex' },
  template: `
    <button
      #trigger
      type="button"
      cdkOverlayOrigin
      #origin="cdkOverlayOrigin"
      [class]="triggerClass()"
      [attr.aria-label]="label()"
      [attr.title]="appearance() === 'icon' ? label() : null"
      aria-haspopup="dialog"
      [attr.aria-expanded]="isOpen()"
      [attr.aria-controls]="isOpen() ? panelId : null"
      [attr.data-testid]="testId()"
      (click)="toggle()"
    >
      <bs-icon [name]="icon()" [size]="appearance() === 'search' ? 18 : 20" />
      @if (appearance() === 'search') {
        <span class="hidden flex-1 text-left text-sm lg:inline">{{ placeholder() }}</span>
        @if (shortcut(); as keys) {
          <kbd
            class="hidden rounded-md bg-white px-2 py-0.5 font-sans text-xs font-semibold text-stone-600 lg:inline dark:bg-stone-900 dark:text-stone-300"
            >{{ keys }}</kbd
          >
        }
      }
      @if (dot()) {
        <span
          class="absolute right-2.5 top-2.5 size-2 rounded-full border-2 border-white bg-bsite-400 dark:border-stone-950"
          aria-hidden="true"
        ></span>
      }
    </button>
    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="origin"
      [cdkConnectedOverlayOpen]="isOpen()"
      [cdkConnectedOverlayPositions]="positions"
      (overlayOutsideClick)="onOutsideClick($event)"
      (overlayKeydown)="onOverlayKeydown($event)"
      (detach)="close()"
    >
      <div
        [id]="panelId"
        role="dialog"
        [attr.aria-labelledby]="headingId"
        cdkTrapFocus
        [cdkTrapFocusAutoCapture]="true"
        tabindex="-1"
        class="w-80 rounded-2xl border border-stone-200 bg-white p-4 shadow-lg dark:border-stone-700 dark:bg-stone-900"
        [attr.data-testid]="testId() ? testId() + '-panel' : null"
      >
        <div class="mb-2 flex items-center justify-between gap-2">
          <h2 [id]="headingId" class="text-base font-semibold text-ink dark:text-stone-50">
            {{ heading() }}
          </h2>
          <button
            type="button"
            class="inline-flex size-9 items-center justify-center rounded-full text-stone-600 hover:bg-stone-100 dark:text-stone-300 dark:hover:bg-stone-800"
            aria-label="Chiudi"
            (click)="close()"
          >
            <bs-icon name="close" [size]="18" />
          </button>
        </div>
        <div class="text-sm text-stone-600 dark:text-stone-300"><ng-content /></div>
      </div>
    </ng-template>
  `,
})
export class PopoverComponent {
  readonly icon = input.required<IconName>();
  /** Nome accessibile del pulsante. */
  readonly label = input.required<string>();
  readonly heading = input.required<string>();
  readonly appearance = input<'icon' | 'search'>('icon');
  /** Testo visibile del pulsante in aspetto `search`. */
  readonly placeholder = input('Cerca');
  /** Scorciatoia mostrata accanto al testo, per esempio "Ctrl K". */
  readonly shortcut = input<string | null>(null);
  readonly dot = input(false);
  readonly testId = input<string | null>(null);

  protected readonly panelId = uniqueId('bs-popover');
  protected readonly headingId = `${this.panelId}-heading`;
  protected readonly positions = POSITIONS;
  readonly isOpen = signal(false);
  private readonly trigger = viewChild.required<ElementRef<HTMLButtonElement>>('trigger');

  protected readonly triggerClass = computed(() =>
    this.appearance() === 'search'
      ? 'relative inline-flex size-10 items-center justify-center gap-2.5 rounded-full text-stone-600 hover:bg-stone-100 lg:h-10 lg:w-90 lg:justify-start lg:bg-stone-100 lg:px-3.5 dark:text-stone-300 dark:hover:bg-stone-800 lg:dark:bg-stone-800'
      : 'relative inline-flex size-10 items-center justify-center rounded-full text-stone-600 hover:bg-stone-100 dark:text-stone-300 dark:hover:bg-stone-800',
  );

  open(): void {
    this.isOpen.set(true);
  }

  toggle(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  close(): void {
    if (!this.isOpen()) {
      return;
    }
    this.isOpen.set(false);
    this.trigger().nativeElement.focus();
  }

  protected onOutsideClick(event: MouseEvent): void {
    // Il clic sul pulsante stesso lo gestisce `toggle`.
    if (!this.trigger().nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  protected onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }
}
